import moment from "moment";
import i18next from "i18next";
import { translate } from "utils";
import { Colors } from "./types";
import { Highlight } from "../../../duck/types";
import { Comment } from "../../../../../duck/types";

const language = localStorage.getItem("language") || "en";

export const getExportHtml = async (
  wholeSectionComments: Comment[],
  sortedList: Highlight[],
  selections: Highlight[],
  withComments: boolean,
): Promise<void> => {
  const title = `${selections[0]?.name} ${selections[0]?.source}`;
  const globalGrid = "8px";
  let comments: string[] = sortedList.map((item) => String(item.comment));
  let dates: string[] = sortedList.map((item) =>
    moment(item.createdAt).format("MMMM DD, YYYY h:mm a"),
  );
  let colors: string[] = sortedList.map((item) => String(item.color));

  if (language !== "en") {
    comments = await translate({
      value: comments,
      languageCode: language,
    });
    dates = await translate({
      value: dates,
      languageCode: language,
    });
    colors = await translate({
      value: colors,
      languageCode: language,
    });
  }

  let html = `<style type='text/css'>
      .yellow {
          background-color: #fbd59c;
      }
      .green {
          background-color: #c2f5cb;
      }
      .blue {
          background-color: #d2e4fe;
      }
      .pink {
          background-color: #fec6b0;
      }
      .headRow {
        border-top: 0;
        border-bottom: calc(${globalGrid} / 2) solid black;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: calc(${globalGrid} * 4);
        -webkit-print-color-adjust: exact;
        color-adjust: exact; 
      }
      caption {
        margin-bottom: calc(${globalGrid} * 2);
      }
      th {
        padding: ${globalGrid};
        text-align: center;
        font-weight: bold;
        border-right: calc(${globalGrid} / 2) solid black;
        border-left: calc(${globalGrid} / 2) solid black;
      }
      th:first-of-type {
        border-left: 0;
      }      
      th:last-of-type {
        border-right: 0;
      }
      td {
        padding: ${globalGrid};
        text-align: center;
        border-right: 1px solid black;
        border-left: 1px solid black;
      }
      td:first-of-type {
        border-left: 0;
      }      
      td:last-of-type {
        border-right: 0;
      }
    </style>`;
  const highlightsTable = `
    <table>
     <caption>${i18next.t(
       "highlighting.highlightsForElementName",
     )} ${title}</caption>
     <tr class="headRow">
      <th>${i18next.t("highlighting.color")}</th>
      <th>${i18next.t("highlighting.highlightedText")}</th>
      ${
        withComments
          ? `<th>
            ${i18next.t("highlighting.note")}
         </th>
         <th>
            ${i18next.t("highlighting.noteCreated")}
         </th>`
          : ""
      }
     </tr>
     ${sortedList
       .map(
         (item: Highlight, i) =>
           `<tr>
              <td class=${item.color}>${colors[i]}</td>
              <td>${item.highlightedText}</td>
              ${
                withComments
                  ? `<td>${comments[i]}</td>
                 <td>${dates[i]}</td>`
                  : ""
              }
            </tr>`,
       )
       .join("")}
  </table>
  `;
  html = `${html}<br/>${highlightsTable}`;

  if (withComments && !!wholeSectionComments.length) {
    comments = wholeSectionComments.map((item) => String(item.comment));
    dates = wholeSectionComments.map((item) =>
      moment(item.createdAt).format("MMMM DD, YYYY h:mm a"),
    );

    if (language !== "en") {
      comments = await translate({
        value: comments,
        languageCode: language,
      });
      dates = await translate({
        value: dates,
        languageCode: language,
      });
    }

    const commentsTable = `
    <table>
     <caption>${i18next.t(
       "highlighting.commentsForElementName",
     )} ${title}</caption>
     <tr class="headRow">
      <th>${i18next.t("highlighting.comment")}</th>
      <th>${i18next.t("highlighting.commentCreated")}</th>
     </tr>
     ${wholeSectionComments
       .map(
         (item, i) =>
           `<tr>
              <td>${comments[i]}</td>
              <td>${dates[i]}</td>
            </tr>`,
       )
       .join("")}
  </table>
  `;

    html = `${html}<br/>${commentsTable}`;
  }

  const newWindow = window.open(
    "",
    "",
    "fullscreen=yes, resizable=yes, menubar=yes, toolbar=yes",
  );
  if (newWindow) {
    newWindow.document.write(`<div>${html}</div>`);
  }
};

export const filterByColor = (
  highlights: Colors,
  colors: string[],
): Highlight[] => {
  let filteredHighlights: Highlight[] = [];

  colors.forEach((item: string) => {
    if (item in highlights) {
      filteredHighlights = filteredHighlights.concat(highlights[item]);
    }
  });
  return filteredHighlights;
};

export const sortByPosition = (
  filteredHighlights: Highlight[],
): Highlight[] => {
  return filteredHighlights.sort((itemA: Highlight, itemB: Highlight) => {
    return itemA.start.sequence - itemB.start.sequence;
  });
};
